/**
 * AI Stuck Inbox (M21.9) — the standalone page the Mission Control
 * "AI escalations" headline card links to.
 *
 * Reuses the M20.4 /mission-control/ai-activity aggregator. The AI
 * (Concierge) handles what it can autonomously; whatever it routes
 * to a human — `route_for_approval` / `advance_step` transitions —
 * lands here as a "stuck" item needing HR attention. Each row links
 * through to the underlying workflow so HR can act on it.
 */

import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { AiActivityItem } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

const _ESCALATION_TRANSITIONS = new Set([
  "route_for_approval",
  "advance_step",
]);

const _WINDOWS: { value: number; labelKey: string }[] = [
  { value: 24, labelKey: "policies.stuck_last_24h" },
  { value: 24 * 7, labelKey: "policies.stuck_last_7d" },
  { value: 24 * 30, labelKey: "policies.stuck_last_30d" },
];

function detailLink(item: AiActivityItem): string {
  switch (item.workflow_type) {
    case "leave_request":
      return "/operations/leave/approvals";
    case "loan_application":
      return "/operations/loans/applications";
    case "excuse_request":
      return "/operations/excuses/queue";
    case "termination":
      return `/operations/eos/${item.workflow_id}`;
    default:
      return "/mission-control";
  }
}

export function StuckInboxPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const [windowHours, setWindowHours] = useState(24);

  const q = useQuery({
    queryKey: ["ai-stuck-inbox", le ?? "_all_", windowHours],
    queryFn: () => api.missionControlAiActivity(le, windowHours),
    refetchInterval: 60_000,
  });

  const escalated = (q.data?.items ?? []).filter((i) =>
    _ESCALATION_TRANSITIONS.has(i.transition),
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("policies.stuck_title")}
        subtitle={t("policies.stuck_subtitle")}
      />

      <div className="flex flex-wrap gap-2">
        {_WINDOWS.map((w) => (
          <button
            key={w.value}
            type="button"
            onClick={() => setWindowHours(w.value)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              windowHours === w.value
                ? "border-brand bg-brand/10 text-brand"
                : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-muted"
            }`}
          >
            {t(w.labelKey)}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : q.data ? (
        <>
          <dl className="grid grid-cols-3 gap-3 rounded-xl border border-border-subtle bg-surface-default p-4 text-xs">
            <div>
              <dt className="text-text-secondary">{t("policies.stuck_handled")}</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums text-text-primary">
                {q.data.summary.handled_count}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t("policies.stuck_escalated")}</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums text-text-primary">
                {q.data.summary.escalated_count}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t("policies.stuck_distinct")}</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums text-text-primary">
                {q.data.summary.distinct_workflows}
              </dd>
            </div>
          </dl>

          {escalated.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              headline={t("policies.stuck_empty_headline")}
              description={t("policies.stuck_empty_body")}
            />
          ) : (
            <ul
              role="list"
              className="divide-y divide-border-subtle rounded-xl border border-border-subtle bg-surface-default"
            >
              {escalated.map((item) => (
                <li key={item.id}>
                  <Link
                    to={detailLink(item)}
                    className="flex flex-col gap-1 px-4 py-3 hover:bg-surface-muted sm:flex-row sm:items-center sm:gap-3"
                  >
                    <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] text-text-secondary">
                      {item.workflow_type}
                    </span>
                    <span className="flex-1 text-sm text-text-primary">
                      {item.transition}{" "}
                      <span className="text-text-secondary">
                        {item.from_state ?? "∅"} → {item.to_state}
                      </span>
                    </span>
                    <span className="font-mono text-[10px] text-text-tertiary">
                      {item.occurred_at.slice(0, 16)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
